/**
 * Centralized QA / admin preview override.
 *
 * Privilege is granted server-side via the `qa_admin` role in
 * `public.user_roles` and resolved through the
 * `is_qa_override_user(p_user_id)` SECURITY DEFINER function. We never
 * ship the list of privileged accounts (or their emails) in client code.
 *
 * IMPORTANT: This override must NEVER award XP, coins, achievements, or
 * write completion records. All reward and persistence paths must check
 * `isQAOverrideUser(user.id)` and short-circuit.
 */
import { supabase } from "@/integrations/supabase/client";

const cache = new Map<string, Promise<boolean>>();

/**
 * Returns whether the given user id has QA override privilege. Cached per
 * user id for the lifetime of the page so callers can `await` cheaply.
 */
export function isQAOverrideUser(userId: string | null | undefined): Promise<boolean> {
  if (!userId) return Promise.resolve(false);
  const hit = cache.get(userId);
  if (hit) return hit;
  const p = (async () => {
    try {
      const { data, error } = await supabase.rpc("is_qa_override_user", {
        p_user_id: userId,
      });
      if (error) return false;
      return data === true;
    } catch {
      return false;
    }
  })();
  cache.set(userId, p);
  return p;
}

/** Clear the QA override cache (e.g., on sign-out). */
export function clearQAOverrideCache(): void {
  cache.clear();
}